import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2, Play, Share2, Download } from "lucide-react";
import "./event.css";

const EventGallery = ({ isOpen, onClose, event }) => {
  // --------------------
  // State & Media Logic
  // --------------------
  const initialVideo = event?.videoUrl || event?.link || "";
  const [activeMedia, setActiveMedia] = useState({
    type: initialVideo ? "video" : "image",
    url: initialVideo || event?.gallery?.[0] || ""
  });
  const [fullImage, setFullImage] = useState(null);

  // --------------------
  // Action Handlers
  // --------------------
  const handleShare = async () => {
    const shareUrl = activeMedia.url;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Moment from ${event.title}`,
          text: "Check out this image from the IHN Event!",
          url: shareUrl,
        });
      } catch (err) {
        console.log("Share failed or cancelled");
      }
    } else {
      // Fallback: Opens WhatsApp
      window.open(`https://wa.me/?text=${encodeURIComponent("Check out this IHN Event photo: " + shareUrl)}`);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(activeMedia.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `IHN-${event.title.replace(/\s+/g, "-")}-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Basic fallback if blob fails
      window.open(activeMedia.url, "_blank");
    }
  };

  // --------------------
  // Auto-scroll Logic
  // --------------------
  const scrollRef = useRef(null);
  const isPaused = useRef(false);
  const resumeTimeout = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const autoScroll = setInterval(() => {
      if (isPaused.current) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollHeight <= clientHeight) return;

      if (scrollTop + clientHeight >= scrollHeight - 2) {
        scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        scrollContainer.scrollTop += 1;
      }
    }, 30);

    return () => clearInterval(autoScroll);
  }, [isOpen]);

  const handleUserInteraction = () => {
    isPaused.current = true;
    if (resumeTimeout.current) clearTimeout(resumeTimeout.current);
    resumeTimeout.current = setTimeout(() => {
      isPaused.current = false;
    }, 2500);
  };

  const getFullEmbed = (url) => {
    if (!url) return "";
    const videoId = url.split("/").pop().split("?")[0].split("&")[0];
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  };

  if (!event || !isOpen) return null;

  return (
    <div className="gallery-modal-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="gallery-modal-container"
      >
        <button className="close-gallery" onClick={onClose}><X size={28} /></button>

        {/* Close button for small devices */}
        <button
          className="close-gallery-mobile"
          onClick={onClose}
        >
          <X size={28} />
        </button>


        <div className="gallery-layout">
          {/* Main Display Area */}
          <div className="gallery-main-display">
            <AnimatePresence mode="wait">
              {activeMedia.type === "video" ? (
                <motion.iframe
                  key="video"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  src={getFullEmbed(activeMedia.url)}
                  className="main-iframe"
                  allow="autoplay; fullscreen"
                />
              ) : (
                <motion.div
                  key={activeMedia.url}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="main-img-container"
                >
                  <img src={activeMedia.url} alt="Gallery" className="main-view-img" />

                  {/* Top Floating Action Bar */}
                  <div className="media-action-bar">
                    <button className="action-btn" onClick={handleShare} title="Share">
                      <Share2 size={18} />
                    </button>
                    <button className="action-btn" onClick={handleDownload} title="Download">
                      <Download size={18} />
                    </button>
                    <button className="action-btn highlight" onClick={() => setFullImage(activeMedia.url)}>
                      <Maximize2 size={18} />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="display-info">
              <h2 className="mx-3">{event.title}</h2>
            </div>
          </div>

          {/* Sidebar Area */}
          <div className="gallery-sidebar">
            <h4 className="sidebar-label">Event Moments</h4>
            <div
              className="vertical-scroll-container"
              ref={scrollRef}
              onScroll={handleUserInteraction}
              onMouseEnter={() => (isPaused.current = true)}
              onMouseLeave={() => (isPaused.current = false)}
            >
              {initialVideo && (
                <div
                  className={`sidebar-thumb-wrapper ${activeMedia.type === "video" ? "active" : ""}`}
                  onClick={() => setActiveMedia({ type: "video", url: initialVideo })}
                >
                  <img src={event.gallery?.[0]} className="sidebar-thumb" alt="video-thumb" />
                  <div style={{ display: "flex", flexDirection: "row" }} className="mx-1">

                    <div className="play-overlay"><Play size={15} fill="white" /></div>
                    <span className="thumb-label mx-2">Video</span>
                  </div>
                </div>
              )}

              {event.gallery?.map((img, idx) => (
                <div
                  key={idx}
                  className={`sidebar-thumb-wrapper ${activeMedia.url === img && activeMedia.type === "image" ? "active" : ""}`}
                  onClick={() => setActiveMedia({ type: "image", url: img })}
                >
                  <img src={img} className="sidebar-thumb" alt={`gallery-${idx}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Fullscreen View Portal */}
      <AnimatePresence>
        {fullImage && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="full-image-viewer"
            onClick={() => setFullImage(null)}
          >
            <button className="exit-full" onClick={() => setFullImage(null)}><X size={40} /></button>
            <img src={fullImage} alt="Full View" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventGallery;